// TelcoTrack — ACL výdeje (GoodsIssue / stock/remove / scan/take)
//
// Neosobní sklad → pouze role `sklad`.
// Osobní sklad → vlastní users.warehouse_id nebo sklady nasdílené aktérovi
// (`sharedWarehouseIds` / user_warehouse_shares). Role sklad = libovolný osobní.
//
// Dva seznamy:
//   A) Převést (scope=transfer) — všechny osobní sklady (půjčka + pending-confirmation).
//      Nesmí používat user_warehouse_shares.
//   B) Materiál / výdej (scope=stock) — vlastní + nasdílené (`user_warehouse_shares`).

import {
  hasEquipmentAccessRole,
  hasOrgAdminRole,
  hasWarehouseApiAccessRole,
  hasWarehouseIssueRole,
} from '../constants/roles.constants';
import {
  isPersonalWarehouse,
  resolveWarehouseUiGroup,
  type WarehouseUiGroup,
  type WarehouseUiGroupInput,
} from './warehouse-ui-group.util';

export const WAREHOUSE_ISSUE_DENIED_BRANCH_CS =
  'Z neosobního skladu smí vydávat materiál pouze role Sklad.';

export const WAREHOUSE_ISSUE_DENIED_OTHER_WORKER_CS =
  'Materiál smíte brát pouze ze svého skladu pracovníků.';

export const WAREHOUSE_ISSUE_DENIED_NO_WORKER_LINK_CS =
  'Nemáte propojený osobní sklad — výdej z osobních skladů není povolen bez vazby.';

export const WAREHOUSE_ISSUE_DENIED_NO_ACCESS_CS =
  'Výdej z neosobního skladu pouze pro roli Sklad.';

export const WAREHOUSE_ISSUE_DENIED_PERSONAL_NO_ACCESS_CS =
  'Nemáte oprávnění k osobním skladům.';

export const WAREHOUSE_ISSUE_DENIED_OTHER_COMPANY_CS =
  'Sklad nepatří vaší firmě — můžete spravovat pouze materiál přidělený vaší firmě.';

export interface WarehouseIssueActor {
  roles: string[] | readonly string[] | null | undefined;
  /** AutoCRM/TT Warehouse.id z users.warehouse_id (osobní sklad). */
  warehouseId?: string | null;
  /**
   * Osobní sklady kolegů, kteří tomuto uživateli nasdíleli svůj sklad
   * (`user_warehouse_shares` → owner.warehouse_id).
   */
  sharedWarehouseIds?: string[] | null;
  /**
   * Sklady přidělené firmě aktéra (osobní sklady uživatelů jeho firmy + firemní sklady).
   * Když je pole definované (`Array.isArray`), omezuje přístup subdodavatele pouze na tyto sklady.
   */
  companyWarehouseIds?: string[] | null;
}

export interface WarehouseIssueAccessResult {
  allowed: boolean;
  reasonCs: string | null;
  warehouseGroup: WarehouseUiGroup;
}

/** GET /warehouses: Materiál (stock) vs Převést picker (transfer). */
export type WarehouseListScope = 'stock' | 'transfer';

export function parseWarehouseListScope(raw?: string | null): WarehouseListScope {
  return String(raw ?? '').trim().toLowerCase() === 'transfer' ? 'transfer' : 'stock';
}

/** Unikátní neprázdná warehouse id (osobní + nasdílené). */
export function normalizeWarehouseIdList(
  ids?: readonly (string | null | undefined)[] | null,
): string[] {
  return [...new Set((ids ?? []).map((id) => String(id ?? '').trim()).filter(Boolean))];
}

/** Osobní sklad aktéra + sklady nasdílené jemu (u org_admin i sklady jeho firmy). */
export function accessiblePersonalWarehouseIds(actor: WarehouseIssueActor): string[] {
  const roles = actor.roles ? [...actor.roles] : [];
  const orgAdminIds = hasOrgAdminRole(roles) ? (actor.companyWarehouseIds ?? []) : [];
  const raw = normalizeWarehouseIdList([
    actor.warehouseId,
    ...(actor.sharedWarehouseIds ?? []),
    ...orgAdminIds,
  ]);
  if (Array.isArray(actor.companyWarehouseIds)) {
    const companySet = new Set(normalizeWarehouseIdList(actor.companyWarehouseIds));
    return raw.filter((id) => companySet.has(id) || id === String(actor.warehouseId ?? '').trim());
  }
  return raw;
}

function isWarehouseInCompanyScope(actor: WarehouseIssueActor, warehouseId: string): boolean {
  if (!Array.isArray(actor.companyWarehouseIds)) return true;
  const id = String(warehouseId ?? '').trim();
  if (!id) return false;
  if (id === String(actor.warehouseId ?? '').trim()) return true;
  const companySet = new Set(normalizeWarehouseIdList(actor.companyWarehouseIds));
  return companySet.has(id);
}

/**
 * Seznam skladů pro Materiál / výdej (GET list default):
 * sklad → vše (nebo jen sklady firmy, pokud má omezený companyWarehouseIds);
 * org_admin → sklady své firmy; terén → vlastní osobní + nasdílené osobní.
 */
export function filterWarehousesVisibleToActor<T extends WarehouseUiGroupInput & { id: string }>(
  actor: WarehouseIssueActor,
  warehouses: readonly T[],
): T[] {
  const roles = actor.roles ? [...actor.roles] : [];
  if (hasWarehouseIssueRole(roles) || hasEquipmentAccessRole(roles)) {
    return warehouses.filter((w) => isWarehouseInCompanyScope(actor, w.id));
  }
  const isOrgAdmin = hasOrgAdminRole(roles);
  const allowed = new Set(accessiblePersonalWarehouseIds(actor));
  return warehouses.filter((warehouse) => {
    const id = String(warehouse.id ?? '').trim();
    if (!isWarehouseInCompanyScope(actor, id)) return false;
    if (isOrgAdmin && allowed.has(id)) return true;
    if (!isPersonalWarehouse(warehouse)) return false;
    return allowed.has(id);
  });
}

/**
 * Seznam skladů pro Převést (zdroj/cíl):
 * sklad → vše (nebo v rámci firmy); terén s warehouse API → osobní sklady (v rámci firmy, pokud je companyWarehouseIds nastaveno).
 */
export function filterWarehousesForTransferPicker<T extends WarehouseUiGroupInput & { id: string }>(
  actor: WarehouseIssueActor,
  warehouses: readonly T[],
): T[] {
  const roles = actor.roles ? [...actor.roles] : [];
  if (hasWarehouseIssueRole(roles) || hasEquipmentAccessRole(roles)) {
    return warehouses.filter((w) => isWarehouseInCompanyScope(actor, w.id));
  }
  if (!hasWarehouseApiAccessRole(roles)) return [];
  const isOrgAdmin = hasOrgAdminRole(roles);
  return warehouses.filter((warehouse) => {
    if (!isWarehouseInCompanyScope(actor, warehouse.id)) return false;
    if (isOrgAdmin) return true;
    return isPersonalWarehouse(warehouse);
  });
}

export function filterWarehousesForListScope<T extends WarehouseUiGroupInput & { id: string }>(
  actor: WarehouseIssueActor,
  warehouses: readonly T[],
  scope?: string | null,
): T[] {
  return parseWarehouseListScope(scope) === 'transfer'
    ? filterWarehousesForTransferPicker(actor, warehouses)
    : filterWarehousesVisibleToActor(actor, warehouses);
}

/**
 * Čtení zásob (ne výdej): terén = osobní v rozsahu firmy (Převést zdroj);
 * neosobní jen sklad (nebo přiřazený sklad firmy pro org_admin). Výdej zůstává vlastní/nasdílené.
 */
export function canReadWarehouseStock(
  actor: WarehouseIssueActor,
  warehouse: WarehouseUiGroupInput & { id: string },
): boolean {
  return filterWarehousesForTransferPicker(actor, [warehouse]).length === 1;
}

/**
 * Výdej / take: neosobní = sklad (nebo firemní sklad v companyWarehouseIds pro org_admin);
 * osobní = vlastní nebo nasdílený (nebo v rámci firmy pro org_admin / sklad).
 */
export function evaluateWarehouseIssueAccess(
  actor: WarehouseIssueActor,
  warehouse: WarehouseUiGroupInput & { id: string },
): WarehouseIssueAccessResult {
  const warehouseGroup = resolveWarehouseUiGroup(warehouse);
  const roles = actor.roles ? [...actor.roles] : [];
  const personal = isPersonalWarehouse(warehouse);
  const warehouseId = String(warehouse.id ?? '').trim();

  if (!isWarehouseInCompanyScope(actor, warehouseId)) {
    return {
      allowed: false,
      reasonCs: WAREHOUSE_ISSUE_DENIED_OTHER_COMPANY_CS,
      warehouseGroup,
    };
  }

  if (personal) {
    if (!hasWarehouseApiAccessRole(roles)) {
      return {
        allowed: false,
        reasonCs: WAREHOUSE_ISSUE_DENIED_PERSONAL_NO_ACCESS_CS,
        warehouseGroup,
      };
    }
    if (hasWarehouseIssueRole(roles)) {
      return { allowed: true, reasonCs: null, warehouseGroup };
    }
    const allowedIds = accessiblePersonalWarehouseIds(actor);
    if (warehouseId && allowedIds.includes(warehouseId)) {
      return { allowed: true, reasonCs: null, warehouseGroup };
    }
    if (!allowedIds.length) {
      return {
        allowed: false,
        reasonCs: WAREHOUSE_ISSUE_DENIED_NO_WORKER_LINK_CS,
        warehouseGroup,
      };
    }
    return {
      allowed: false,
      reasonCs: WAREHOUSE_ISSUE_DENIED_OTHER_WORKER_CS,
      warehouseGroup,
    };
  }

  if (
    hasOrgAdminRole(roles) &&
    Array.isArray(actor.companyWarehouseIds) &&
    warehouseId &&
    normalizeWarehouseIdList(actor.companyWarehouseIds).includes(warehouseId)
  ) {
    return { allowed: true, reasonCs: null, warehouseGroup };
  }

  if (!hasWarehouseIssueRole(roles)) {
    return {
      allowed: false,
      reasonCs: WAREHOUSE_ISSUE_DENIED_NO_ACCESS_CS,
      warehouseGroup,
    };
  }

  return { allowed: true, reasonCs: null, warehouseGroup };
}

/** true = actor smí vydat ze skladu (bez message). */
export function canIssueStockFromWarehouse(
  actor: WarehouseIssueActor,
  warehouse: WarehouseUiGroupInput & { id: string },
): boolean {
  return evaluateWarehouseIssueAccess(actor, warehouse).allowed;
}
